import { useState, useEffect } from 'react';

import { Button, Icon } from '@skillarea/lib-frontend';
import { Link } from '@tanstack/react-router';
import { formatMoney } from '@skillarea/lib-shared';
import { useTranslation } from 'react-i18next';

import cx from 'classnames';

import styles from './Header.module.css';

import { AccountMenu } from '~/components/Account/AccountMenu/AccountMenu';
import { LanguageSwitcher } from '~/components/LanguageSwitcher/LanguageSwitcher';
import { RollingMoneyDigits } from '~/components/BalanceRolling/RollingMoneyDigits';

interface IHeaderProps {
  activeLocale: string;
  isAuthenticated?: boolean;
  isBalanceLoading?: boolean;
  user?: {
    name: string;
    avatar?: string;
    balance: number;
  };
  onLocaleChange: (locale: string) => void | Promise<void>;
  onLoginClick?: () => void;
  onRegisterClick?: () => void;
  onLogoutClick?: () => void;
}

export function Header({
  activeLocale,
  isAuthenticated = false,
  isBalanceLoading = false,
  onLocaleChange,
  onLoginClick,
  onLogoutClick,
  onRegisterClick,
  user,
}: IHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t: tOnboard } = useTranslation('onboard');
  const { t } = useTranslation('layout');
  const loginLabel = tOnboard('login');
  const signUpLabel = tOnboard('register.title');
  const gamesLabel = t('nav.games');
  const logoutLabel = t('header.logout');

  const closeMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    if (!isMenuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMenuOpen]);

  return (
    <>
      <header className={styles.header}>
        <div className={styles.container}>
          <Link className={styles.logo} to="/">
            <img
              alt={t('brand.logoAlt')}
              className={styles.logoImage}
              src="/logo.svg"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className={styles.desktopNav}>
            <LanguageSwitcher
              activeLocale={activeLocale}
              onLocaleChange={onLocaleChange}
            />

            <div className={styles.divider} />

            <Button
              as="link"
              color="primary"
              iconRight={
                <img alt="" height={20} src="/icon-game.svg" width={29} />
              }
              size="s"
              to="/games"
              variant="filled"
            >
              {gamesLabel}
            </Button>

            <div className={styles.divider} />

            {isAuthenticated && user ? (
              <>
                <Link className={styles.profileLink} to="/account/profile">
                  <div className={styles.avatar}>
                    {user.avatar ? (
                      <img
                        alt={user.name}
                        className={styles.avatarImage}
                        src={user.avatar}
                      />
                    ) : (
                      <div className={styles.avatarPlaceholder}>
                        {user.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <span className={styles.userName}>{user.name}</span>
                </Link>

                <div className={styles.divider} />

                <Button
                  as="link"
                  color="accent"
                  iconLeft={
                    <img alt="" height={20} src="/icon-coin.svg" width={22} />
                  }
                  size="s"
                  to="/account/balance"
                  variant="filled"
                >
                  {isBalanceLoading ? (
                    <span
                      aria-busy
                      aria-label={t('header.aria.loadingBalance')}
                      className={styles.headerBalanceRolling}
                    >
                      <RollingMoneyDigits />
                    </span>
                  ) : (
                    formatMoney(user?.balance, { maxDigits: 0 })
                  )}
                </Button>

                <div className={styles.divider} />

                <button
                  aria-label={t('header.aria.logout')}
                  className={styles.logoutButton}
                  onClick={onLogoutClick}
                  type="button"
                >
                  <Icon color="contrast" img="logout" size="xl" />
                </button>
              </>
            ) : (
              <>
                <Button
                  iconLeft={
                    <img alt="" height={14} src="/icon-robot.svg" width={14} />
                  }
                  isDividerVisible={false}
                  onClick={onRegisterClick}
                  size="s"
                  variant="ghost"
                >
                  {signUpLabel}
                </Button>

                <div className={styles.divider} />

                <Button
                  color="accent"
                  iconRight={
                    <img alt="" height={15} src="/icon-rocket.svg" width={20} />
                  }
                  onClick={onLoginClick}
                  size="s"
                  variant="filled"
                >
                  {loginLabel}
                </Button>
              </>
            )}
          </nav>

          {/* Tablet & Mobile Navigation */}
          <div className={styles.mobileNav}>
            <Button
              as="link"
              className={styles.buttonMobile}
              color="primary"
              iconRight={
                <img
                  alt={t('header.alt.games')}
                  height={20}
                  src="/icon-game.svg"
                  width={29}
                />
              }
              isDividerVisible={false}
              size="s"
              to="/games"
              variant="filled"
            >
              <span className={styles.buttonTextHiddenMobile}>
                {gamesLabel}
              </span>
            </Button>

            {isAuthenticated && user ? (
              <Button
                as="link"
                className={styles.buttonMobile}
                color="accent"
                iconLeft={
                  <img
                    alt={t('header.alt.balance')}
                    height={20}
                    src="/icon-coin.svg"
                    width={22}
                  />
                }
                isDividerVisible={false}
                size="s"
                to="/account/balance"
                variant="filled"
              >
                <span className={styles.buttonTextHiddenMobile}>
                  {isBalanceLoading ? (
                    <span
                      aria-busy
                      aria-label={t('header.aria.loadingBalance')}
                      className={styles.headerBalanceRolling}
                    >
                      <RollingMoneyDigits />
                    </span>
                  ) : (
                    formatMoney(user?.balance, { maxDigits: 0 })
                  )}
                </span>
              </Button>
            ) : (
              <Button
                className={styles.buttonMobile}
                color="accent"
                iconRight={
                  <img
                    alt={t('header.alt.login')}
                    height={15}
                    src="/icon-rocket.svg"
                    width={20}
                  />
                }
                isDividerVisible={false}
                onClick={onLoginClick}
                size="s"
                variant="filled"
              >
                <span className={styles.buttonTextHiddenMobile}>
                  {loginLabel}
                </span>
              </Button>
            )}

            <button
              aria-label={t('header.aria.menu')}
              className={styles.burgerButton}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              type="button"
            >
              <span className={styles.burgerLineTop} />
              <span className={styles.burgerLineMiddle} />
              <span className={styles.burgerLineBottom} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      <div
        className={cx(styles.menuOverlay, isMenuOpen && styles.menuOverlayOpen)}
      >
        <button
          aria-label={t('header.aria.closeOverlay')}
          className={cx(
            styles.menuBackdrop,
            isMenuOpen && styles.menuBackdropOpen,
          )}
          onClick={closeMenu}
          type="button"
        />
        <div
          className={cx(styles.menuDrawer, isMenuOpen && styles.menuDrawerOpen)}
        >
          <button
            aria-label={t('header.aria.closeMenu')}
            className={styles.menuCloseButton}
            onClick={closeMenu}
            type="button"
          >
            <span className={styles.burgerLineTopOpen} />
            <span className={styles.burgerLineMiddleOpen} />
            <span className={styles.burgerLineBottomOpen} />
          </button>

          <Link className={styles.menuLogo} onClick={closeMenu} to="/">
            <img
              alt={t('brand.logoAlt')}
              className={styles.menuLogoImage}
              src="/logo.svg"
            />
          </Link>

          <div className={styles.menuLanguage}>
            <LanguageSwitcher
              activeLocale={activeLocale}
              className={styles.menuLanguageSelect}
              onLocaleChange={onLocaleChange}
            />
          </div>

          {isAuthenticated && user ? (
            <>
              <div className={styles.menuUserBlock}>
                <div className={styles.menuAvatar}>
                  {user.avatar ? (
                    <img
                      alt={user.name}
                      className={styles.avatarImage}
                      src={user.avatar}
                    />
                  ) : (
                    <div className={styles.avatarPlaceholder}>
                      {user.name.charAt(0)}
                    </div>
                  )}
                </div>
                <span className={styles.menuUserName}>{user.name}</span>
              </div>

              <div className={styles.menuButtons}>
                <Button
                  as="link"
                  color="primary"
                  iconRight={
                    <img alt="" height={20} src="/icon-game.svg" width={29} />
                  }
                  onClick={closeMenu}
                  size="s"
                  to="/games"
                  variant="filled"
                >
                  {gamesLabel}
                </Button>

                <Button
                  as="link"
                  className={styles.menuBalanceButton}
                  color="accent"
                  iconLeft={
                    <img alt="" height={20} src="/icon-coin.svg" width={22} />
                  }
                  onClick={closeMenu}
                  size="s"
                  to="/account/balance"
                  variant="filled"
                >
                  {isBalanceLoading ? (
                    <span
                      aria-busy
                      aria-label={t('header.aria.loadingBalance')}
                      className={styles.headerBalanceRolling}
                    >
                      <RollingMoneyDigits />
                    </span>
                  ) : (
                    formatMoney(user?.balance, { maxDigits: 0 })
                  )}
                </Button>
              </div>

              <div className={styles.menuDivider} />

              <AccountMenu className={styles.menuNav} onLinkClick={closeMenu} />

              <div className={styles.menuDivider} />

              <Button
                className={styles.menuLogout}
                iconRight={
                  <Icon
                    className={styles.menuLogoutIcon}
                    color="accent"
                    img="logout"
                    size="xl"
                  />
                }
                onClick={() => {
                  closeMenu();
                  onLogoutClick?.();
                }}
                variant="ghost"
              >
                {logoutLabel}
              </Button>
            </>
          ) : (
            <>
              <div className={styles.menuAuthButtons}>
                <Button
                  iconLeft={
                    <img alt="" height={14} src="/icon-robot.svg" width={14} />
                  }
                  isDividerVisible={false}
                  onClick={() => {
                    closeMenu();
                    onRegisterClick?.();
                  }}
                  variant="filled"
                >
                  {signUpLabel}
                </Button>

                <Button
                  color="accent"
                  iconRight={
                    <img alt="" height={15} src="/icon-rocket.svg" width={20} />
                  }
                  isDividerVisible={false}
                  onClick={() => {
                    closeMenu();
                    onLoginClick?.();
                  }}
                  variant="filled"
                >
                  {loginLabel}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
